import React, { useState } from 'react';
import {
  Box,
  Button,
  useToast,
  FormControl,
  FormLabel,
  Input,
  Select,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  HStack,
  Image,
  Flex,
} from '@chakra-ui/react';
import axios from 'axios';

import W from '../../images/games/icons/W.png';
import U from '../../images/games/icons/U.png';
import B from '../../images/games/icons/B.png';
import R from '../../images/games/icons/R.png';
import G from '../../images/games/icons/G.png';
import C from '../../images/games/icons/C.png';

import ParticipantsList from './ParticipantsList';

function CreateGame({ onDrawerClose, onGameCreated }) {
  const toast = useToast();

  const [gameName, setGameName] = useState('');
  const [format, setFormat] = useState('Commander');
  const [tableName, setTableName] = useState('');
  const [initialScore, setInitialScore] = useState('');
  const [createGameError, setCreateGameError] = useState('');

  const [participants, setParticipants] = useState([]);

  const [loading, setLoading] = useState(false);

  const [newParticipantName, setNewParticipantName] = useState('');
  const [selectedColors, setSelectedColors] = useState([]);

  const colorOptions = [
    { code: 'W', img: W, label: 'White' },
    { code: 'U', img: U, label: 'Blue' },
    { code: 'B', img: B, label: 'Black' },
    { code: 'R', img: R, label: 'Red' },
    { code: 'G', img: G, label: 'Green' },
    { code: 'C', img: C, label: 'Colorless' },
  ];

  const toggleColor = (colorCode) => {
    setSelectedColors((prev) => {
      if (prev.includes(colorCode)) {
        return prev.filter((c) => c !== colorCode);
      }
      return [...prev, colorCode];
    });
  };

  const handleAddParticipant = () => {
    const nameTrim = newParticipantName.trim();
    if (!nameTrim) {
      toast({
        title: 'Nome mancante',
        description: 'Inserisci il numero tessera del partecipante',
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      return;
    }
    const newParticipant = {
      id: Date.now(), // Unique identifier
      cardNumber: nameTrim,
      colors: selectedColors,
    };
    setParticipants((prev) => [...prev, newParticipant]);
    setNewParticipantName('');
    setSelectedColors([]);
  };

  const handleRemoveParticipant = (participantId) => {
    setParticipants((prev) => prev.filter((p) => p.id !== participantId));
  };

  const resetForm = () => {
    setGameName('');
    setFormat('Commander');
    setTableName('');
    setInitialScore('');
    setParticipants([]);
    setNewParticipantName('');
    setSelectedColors([]);
  };

  const handleStartGame = async () => {
    if (!gameName) {
      toast({
        title: 'Errore',
        description: 'Nome della partita obbligatorio',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setLoading(true);
    // Extract card numbers from participants
    const cardNumbers = participants.map((p) => ({
      cardNumber: p.cardNumber,
      colors: p.colors,
    }));
    try {
      await axios.post('/api/games/start', {
        Name: gameName,
        Format: format,
        Table_Name: tableName,
        initialScore: Number(initialScore),
        cardNumbers,
      });

      toast({
        title: 'Partita Creata',
        description: `La partita "${gameName}" è stata creata con successo!`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      resetForm();
      onGameCreated();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(error.response?.data?.error || error.message);
      setCreateGameError(error.response?.data?.error || error.message);
    } finally {
      setLoading(false);
    }
  };

  const createGameDisabled =
    !participants.length || !gameName || !format || !initialScore;

  return (
    <Drawer isOpen onClose={onDrawerClose} placement="right" size="sm">
      <DrawerOverlay />
      <DrawerContent bg="purple.400" borderTopRadius="md" color="white">
        <DrawerHeader borderBottomWidth="1px">Crea Nuova Partita</DrawerHeader>
        <DrawerBody pb={4} bg="purple.600">
          {/* New Participant: card number & color icons */}
          <FormControl mb={2} bg="purple.600" p={3} borderRadius="md" mt={4}>
            <Flex flexDirection="column">
              <Box>
                <FormLabel>Nuovo Partecipante (Numero Tessera)</FormLabel>
                <Input
                  placeholder="Es. 251982"
                  value={newParticipantName}
                  onChange={(e) => setNewParticipantName(e.target.value)}
                  bg="white"
                  color="black"
                  mb={2}
                />
              </Box>
              <HStack
                spacing={2}
                wrap="wrap"
                display="flex"
                mt={1}
                justify="center"
                width="100%"
                align="center"
              >
                {colorOptions.map((option) => {
                  const isSelected = selectedColors.includes(option.code);
                  return (
                    <Box
                      key={option.code}
                      cursor="pointer"
                      borderRadius="md"
                      borderWidth={isSelected ? '2px' : '2px'}
                      borderColor={isSelected ? 'blue.200' : 'transparent'}
                      p={1}
                      onClick={() => toggleColor(option.code)}
                    >
                      <Image
                        src={option.img}
                        alt={option.label}
                        width={{ base: '24px', sm: '32px' }}
                        height={{ base: '24px', sm: '32px' }}
                      />
                    </Box>
                  );
                })}
              </HStack>
            </Flex>
            <Button
              colorScheme="blue"
              onClick={handleAddParticipant}
              mt={4}
              size="sm"
              display="flex"
              justifySelf="flex-end"
              _active={{ transform: 'scale(.97)' }}
              disabled={!newParticipantName || !selectedColors.length}
            >
              Aggiungi
            </Button>
          </FormControl>
          {Boolean(participants.length) && (
            <ParticipantsList
              participants={participants}
              onRemove={handleRemoveParticipant}
            />
          )}
          {/* Nome Partita */}
          <FormControl mb={3} mt={2}>
            <FormLabel>Nome Partita</FormLabel>
            <Input
              placeholder="Es. Friday Night Magic"
              value={gameName}
              onChange={(e) => setGameName(e.target.value)}
              bg="white"
              color="black"
            />
          </FormControl>

          <FormControl mb={3}>
            <FormLabel>Formato</FormLabel>
            <Select
              value={format}
              onChange={(e) => setFormat(e.target.value)}
              bg="white"
              color="black"
            >
              <option value="Commander">Commander</option>
              <option value="Standard">Standard</option>
              <option value="Modern">Modern</option>
            </Select>
          </FormControl>

          <FormControl mb={3}>
            <FormLabel>Tavolo</FormLabel>
            <Input
              placeholder="Nome del tavolo"
              value={tableName}
              onChange={(e) => setTableName(e.target.value)}
              bg="white"
              color="black"
            />
          </FormControl>

          <FormControl mb={3}>
            <FormLabel>Punteggio Iniziale</FormLabel>
            <Input
              placeholder="Il punteggio a inizio partita"
              value={initialScore}
              onChange={(e) => setInitialScore(e.target.value)}
              bg="white"
              color="black"
              type="number"
            />
          </FormControl>
        </DrawerBody>
        <DrawerFooter
          alignItems="flex-end"
          borderTopWidth="1px"
          flexDirection="column"
        >
          {Boolean(createGameError) && (
            <Flex
              width="100%"
              justify="center"
              px={3}
              py={2}
              fontSize="sm"
              mb={4}
              bg="red.400"
              borderRadius="sm"
            >
              {createGameError}
            </Flex>
          )}
          <Flex>
            <Button
              variant="outline"
              color="white"
              mr={3}
              onClick={onDrawerClose}
              _active={{ transform: 'scale(.97)' }}
            >
              Annulla
            </Button>
            <Button
              colorScheme="green"
              onClick={handleStartGame}
              _active={{ transform: 'scale(.97)' }}
              disabled={createGameDisabled || loading}
              isLoading={loading}
            >
              Avvia Partita
            </Button>
          </Flex>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

export default CreateGame;
